import type { DiaryEntry, LatLon, Stage } from '../../lib/types'
import { fitProjection, toPolygonPoints, type Projection } from '../map/projection'
import { stageTraces } from '../map/routeGeoJson'

/**
 * Die Rückfallebene für den Kartenausschnitt einer Etappe: eine Skizze.
 *
 * Sie steht da, wo der Bildexport aus MapLibre nicht zustande kam - kein WebGL,
 * kein Kachelarchiv, kein Netz, oder der Ausschnitt lief in die Zeitgrenze.
 * Eine leere Fläche ist im gedruckten Buch die schlechteste aller Antworten:
 * sie sieht aus wie ein Fehler und lässt sich mit nichts erklären. Eine
 * Skizze dagegen sagt genau das, was sie zeigt - von hier nach dort, so
 * verläuft die Strasse.
 *
 * Bewusst nicht die grosse schematische Karte aus features/map: die legt die
 * ganze Region samt Küstenlinien in den Ausschnitt und zeigt für einen
 * Standtag von 60 Kilometern den halben Balkan. Hier wird nur diese eine Etappe
 * eingerahmt.
 *
 * Alles daran ist SVG mit Strichen und Flächen im Vordergrund. Das ist kein
 * Zufall: Hintergrundfarben druckt der Browser nur, wenn im Druckdialog
 * "Hintergrundgrafiken" angehakt ist, SVG-Füllungen und -Striche dagegen immer.
 * Die Skizze kommt also auch dann aufs Papier, wenn jemand den Hinweis im
 * Vorbereitungsschirm überlesen hat.
 */
export function StageSketch({
  stage,
  entry,
  driven,
}: {
  stage: Stage
  entry: DiaryEntry
  driven: boolean
}) {
  const breite = 480
  const hoehe = 165

  // Dieselben Spuren und dieselbe Reihenfolge wie auf der echten Karte: die
  // zurückhaltende Planung zuerst, die aufgezeichnete Wirklichkeit darüber.
  const spuren = stageTraces(stage, entry)
  const istVergleich = spuren.length > 1
  const vorderste = spuren.at(-1)!
  const punkte: LatLon[] = spuren.flatMap((spur) => spur.points)
  const project = fitProjection([...punkte], breite, hoehe, 22)

  const [x1, y1] = project(stage.c1[0], stage.c1[1])
  const [x2, y2] = project(stage.c2[0], stage.c2[1])
  // Der Tagebuchhaken und sonst nichts. Hier stand zusätzlich istVergleich,
  // und damit war der Zielpunkt einer Etappe schon deshalb rot, weil eine
  // Aufzeichnung danebenlag - dieselbe Verwechslung, die auf der Karte
  // RouteLineProps.betont von driven trennt. Für die Linien im Vergleich zählt
  // die Aufzeichnung, für den Punkt am Ziel zählt, was der Reisende abgehakt
  // hat; im Vergleich zeichnen ohnehin die Spuren ihre eigene Farbe.
  const farbe = driven ? 'var(--marsala)' : 'var(--asphalt)'

  return (
    <svg
      className="dr-skizze"
      viewBox={`0 0 ${String(breite)} ${String(hoehe)}`}
      role="img"
      aria-label={`Skizze der Etappe ${stage.from} nach ${stage.to}`}
    >
      <rect width={breite} height={hoehe} fill="var(--atlas)" />

      {/*
        Im Vergleich ist die Planung fein und dunkel, die Fahrt marsalarot,
        kräftig und von einer breiten Hülle unterstrichen. Die Hierarchie ist
        dieselbe wie im MapLibre-Stil und braucht deshalb keine Legende.
      */}
      {istVergleich ? (
        spuren.map(({ points: spur, kind }) => {
          const aufgezeichnet = kind === 'track'
          const stroke = aufgezeichnet ? 'var(--marsala)' : 'var(--asphalt)'
          return (
            <g key={kind} data-spur={aufgezeichnet ? 'gefahren' : 'geplant'}>
              {aufgezeichnet && (
                <polyline
                  points={toPolygonPoints(spur, project)}
                  fill="none"
                  stroke={stroke}
                  strokeWidth={10}
                  strokeOpacity={0.25}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              )}
              <polyline
                points={toPolygonPoints(spur, project)}
                fill="none"
                stroke={stroke}
                strokeWidth={aufgezeichnet ? 4.2 : 2.2}
                strokeOpacity={aufgezeichnet ? 1 : 0.72}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
          )
        })
      ) : vorderste.kind !== 'air' ? (
        <>
          {vorderste.kind === 'track' && (
            <polyline
              points={toPolygonPoints(vorderste.points, project)}
              fill="none"
              stroke={farbe}
              strokeWidth={9}
              strokeOpacity={0.25}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
          <polyline
            points={toPolygonPoints(vorderste.points, project)}
            fill="none"
            stroke={farbe}
            strokeWidth={3}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      ) : (
        /*
         * Die Luftlinie als flacher Bogen und nicht als Strich.
         *
         * Nicht Schönfärberei, sondern die genauere Aussage: eine gerade Linie
         * zwischen zwei Orten sieht aus wie ein Weg, den es so nicht gibt. Der
         * Bogen ist die alte Konvention für "Verbindung, nicht Strecke" - und
         * ohne ihn fällt eine Ost-West-Etappe zu einem waagrechten Strich
         * zusammen, weil die Projektion massstabsgetreu bleibt.
         *
         * Ein Bogen je Abschnitt und nicht einer von Start nach Ziel: liegen
         * Zwischenziele dazwischen, ist die Luftlinie ein Zug über sie hinweg,
         * und bei einer Runde wäre der Bogen von Start zu Ziel gar keiner.
         */
        <path
          d={bogenzug(vorderste.points, project, hoehe * 0.18)}
          fill="none"
          stroke={farbe}
          strokeWidth={2.4}
          strokeDasharray="7 5"
          strokeLinecap="round"
        />
      )}

      <circle cx={x1} cy={y1} r={5} fill="var(--paper)" stroke="var(--asphalt)" strokeWidth={2} />
      <circle cx={x2} cy={y2} r={6} fill={farbe} stroke="var(--asphalt)" strokeWidth={2} />

      <text className="dr-skizze-name" x={x1} y={y1 - 11} textAnchor="middle">
        {stage.from}
      </text>
      <text className="dr-skizze-name" x={x2} y={y2 + 20} textAnchor="middle">
        {stage.to}
      </text>
    </svg>
  )
}

/**
 * Die Luftlinie über eine Folge von Punkten: ein Bogen je Abschnitt.
 *
 * Bei zwei Punkten ist es genau der eine Bogen von früher. Bei einer Runde -
 * Start, drei Zwischenziele, wieder der Start - sind es vier, und erst
 * zusammen sagen sie, worum es geht: eine Fahrt, die dorthin zurückkehrt, wo
 * sie begann.
 */
function bogenzug(punkte: readonly LatLon[], project: Projection, maxVersatz: number): string {
  const gezeichnet = punkte.map(([lat, lon]) => project(lat, lon))
  const erste = gezeichnet[0]
  if (!erste) return ''

  const stuecke: string[] = []
  for (let i = 1; i < gezeichnet.length; i++) {
    const [x1, y1] = gezeichnet[i - 1]!
    const [x2, y2] = gezeichnet[i]!
    stuecke.push(bogen(x1, y1, x2, y2, maxVersatz))
  }
  // Nur ein Punkt: der Weg beginnt und endet dort. Das ist der Standtag, und
  // "M x y" ist genau seine Aussage - kein Strich, keine Schlaufe.
  return stuecke.length > 0 ? stuecke.join(' ') : `M ${String(erste[0])} ${String(erste[1])}`
}

/**
 * Ein flacher Bogen von einem Punkt zum anderen.
 *
 * Der Kontrollpunkt liegt in der Mitte, senkrecht zur Verbindung versetzt, und
 * zwar immer nach oben - so haben alle Etappen des Buchs dieselbe Handschrift,
 * egal ob es nach Osten oder nach Westen geht. Der Versatz ist gedeckelt: ein
 * Sechstel einer sehr langen Strecke führte den Bogen aus der Zeichenfläche
 * heraus.
 *
 * Bei zwei Punkten am selben Ort, also einem Standtag, bleibt es beim Punkt: ein
 * Bogen von A nach A wäre eine Schlaufe, die eine Fahrt behauptet, die es nicht
 * gab.
 */
function bogen(x1: number, y1: number, x2: number, y2: number, maxVersatz: number): string {
  const dx = x2 - x1
  const dy = y2 - y1
  const laenge = Math.hypot(dx, dy)
  if (laenge < 1) return `M ${String(x1)} ${String(y1)}`

  const versatz = Math.min(laenge / 6, maxVersatz)
  // Die Senkrechte auf die Verbindung, in der Fassung, die nach oben zeigt. In
  // SVG wächst y nach unten, oben ist also die negative Richtung.
  let nx = dy / laenge
  let ny = -dx / laenge
  if (ny > 0) {
    nx = -nx
    ny = -ny
  }

  const cx = (x1 + x2) / 2 + nx * versatz
  const cy = (y1 + y2) / 2 + ny * versatz

  return `M ${String(x1)} ${String(y1)} Q ${String(cx)} ${String(cy)} ${String(x2)} ${String(y2)}`
}
